import { isRecord } from '@/content/schema';

/**
 * Content saved in an older shape, brought up to date when the CMS opens the page, so the form shows it as the site
 * does. The site reads old content the same way (web/src/content/bigger-picture.ts, upgradeBiggerPicture;
 * web/src/content/archives.ts, upgradeQuotes).
 */

/**
 * Before stories in "The bigger picture" had their own title (deCheckers' feedback), every title repeated the series,
 * e.g. "Desinformatie doorheen de geschiedenis" with the story in "label", or "Praktische gids: Hoe AI werkt". The
 * label becomes the title; otherwise the shared "…: " in front of the titles becomes the series' prefix.
 */
function upgradeSeries(series: unknown): unknown {
  if (!isRecord(series) || 'titlePrefix' in series || !Array.isArray(series.items)) return series;
  const stories = series.items.filter(isRecord);
  const labelOf = (label: unknown): string => (typeof label === 'string' ? label.trim() : '');
  const prefixes = new Set(
    stories.filter((story) => !labelOf(story.label)).map((story) => {
      const title = String(story.title ?? '');
      return title.includes(': ') ? title.slice(0, title.indexOf(': ')) : null;
    }),
  );
  const [first] = prefixes;
  const prefix = prefixes.size === 1 && typeof first === 'string' ? first : '';
  return {
    ...series,
    titlePrefix: prefix,
    items: stories.map(({ label, ...story }) => {
      if (labelOf(label)) return { ...story, title: labelOf(label) };
      return prefix && typeof story.title === 'string' ? { ...story, title: story.title.slice(prefix.length + 2) } : story;
    }),
  };
}

/**
 * Before quotes had a field for who the author is, it followed the name after a comma: "Djuna Barnes, Amerikaanse
 * schrijfster". Quotes saved without the field are split at that comma.
 */
function upgradeQuote(quote: unknown): unknown {
  if (!isRecord(quote) || 'authorDescription' in quote || typeof quote.author !== 'string') return quote;
  const comma = quote.author.indexOf(', ');
  if (comma < 0) return quote;
  return { ...quote, author: quote.author.slice(0, comma).trim(), authorDescription: quote.author.slice(comma + 2).trim() };
}

/**
 * Not an older shape: the site lists the glossary alphabetically (web/src/components/content/GlossaryBrowser.tsx), so
 * the form shows the terms in the same Dutch order instead of the order they were added in. A new term stays at the
 * bottom until the page is opened again.
 */
function sortGlossary(saved: unknown): unknown {
  if (!isRecord(saved) || !Array.isArray(saved.terms)) return saved;
  const termOf = (item: unknown): string => (isRecord(item) && typeof item.term === 'string' ? item.term : '');
  return { ...saved, terms: [...saved.terms].sort((a, b) => termOf(a).localeCompare(termOf(b), 'nl')) };
}

const UPGRADES: Record<string, (saved: unknown) => unknown> = {
  woordenboek: sortGlossary,
  'the-bigger-picture': (saved) => (isRecord(saved) && Array.isArray(saved.series) ? { ...saved, series: saved.series.map(upgradeSeries) } : saved),
  quotes: (saved) => (isRecord(saved) && Array.isArray(saved.items) ? { ...saved, items: saved.items.map(upgradeQuote) } : saved),
};

export function upgradeContent(slug: string, saved: unknown): unknown {
  const upgrade = UPGRADES[slug];
  return upgrade ? upgrade(saved) : saved;
}
